import React, { useEffect, useState, useCallback } from 'react';
import { getApiUrl } from '../utils/apiConfig';

const API_URL = getApiUrl();
const DEFAULT_AVATAR = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect width="80" height="80" fill="#2a3040"/><circle cx="40" cy="30" r="14" fill="#555"/><ellipse cx="40" cy="68" rx="24" ry="18" fill="#555"/></svg>');

export default function AdsReview() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem('ads_review_key') || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [devToken, setDevToken] = useState(() => sessionStorage.getItem('dev_token') || '');
  const [searchQuery, setSearchQuery] = useState('');

  const hdrs = useCallback(() => ({ 'x-admin-key': adminKey }), [adminKey]);
  const api = useCallback((path) => fetch(`${API_URL}${path}`, { headers: hdrs() }).then(r => r.json()), [hdrs]);
  const apiPost = useCallback((path, body) => fetch(`${API_URL}${path}`, { method: 'POST', headers: { ...hdrs(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json()), [hdrs]);

  const handleLogin = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/google-tokens`, { headers: { 'x-admin-key': adminKey } });
      if (!resp.ok) throw new Error('Invalid key');
      const data = await resp.json();
      setTokens(data.data || []);
      setAuthenticated(true);
      sessionStorage.setItem('ads_review_key', adminKey);
      fetchBookings();
    } catch { setError('Invalid admin key'); }
  };

  const fetchBookings = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/bookings`, { headers: { 'x-admin-key': adminKey } });
      if (resp.ok) { const d = await resp.json(); setBookings(d.data || []); }
    } catch {}
  };

  const loadReview = async (token) => {
    setSelectedToken(token);
    setLoading(true);
    setError(null);
    setReviewData(null);
    try {
      const [profile, customers] = await Promise.all([
        api(`/api/google/${token.id}/profile`).catch(() => ({})),
        api(`/api/google/${token.id}/ads/customers`).catch(() => ({ resourceNames: [] })),
      ]);
      const customerIds = (customers.resourceNames || []).map(r => r.replace('customers/', ''));
      const booking = bookings.find(b => b.email === token.email) || null;
      setReviewData({ profile, customerIds, token, booking });
    } catch (err) { setError('Failed: ' + err.message); }
    setLoading(false);
  };

  const refreshAll = async () => {
    try {
      const resp = await fetch(`${API_URL}/api/google-tokens`, { headers: hdrs() });
      const data = await resp.json();
      setTokens(data.data || []);
    } catch {}
    fetchBookings();
  };

  useEffect(() => {
    if (authenticated) {
      const i = setInterval(refreshAll, 15000);
      return () => clearInterval(i);
    }
  }, [authenticated]);

  if (!authenticated) {
    return (
      <div style={S.page}>
        <div style={S.loginCard}>
          <h2 style={{ margin: '0 0 8px', color: '#fff', fontSize: '20px' }}>Ads Performance Review</h2>
          <p style={{ color: '#888', fontSize: '13px', marginBottom: '24px' }}>Recruiter access only</p>
          {error && <p style={{ color: '#e53e3e', fontSize: '13px', marginBottom: '12px' }}>{error}</p>}
          <input type="password" value={adminKey} onChange={e => { setAdminKey(e.target.value); setError(null); }} onKeyDown={e => e.key === 'Enter' && handleLogin()} placeholder="Admin Key" style={S.input} />
          <button onClick={handleLogin} style={S.loginBtn}>Access Panel</button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.header}>
          <div>
            <h1 style={{ margin: 0, fontSize: '18px', color: '#fff' }}>Candidate Evaluation Dashboard</h1>
            <span style={{ fontSize: '12px', color: '#aaa' }}>Google Ads Performance Review</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="text" value={devToken} onChange={e => { setDevToken(e.target.value); sessionStorage.setItem('dev_token', e.target.value); }} placeholder="Developer Token (optional)" style={{ ...S.input, width: '220px', marginBottom: 0, fontSize: '12px', padding: '6px 10px' }} />
            <button onClick={refreshAll} style={S.refreshBtn}>↻ Refresh</button>
          </div>
        </div>

        <div style={S.layout}>
          <div style={S.sidebar}>
            <h3 style={S.sidebarTitle}>CANDIDATES ({tokens.length})</h3>
            <div style={{ padding: '0 8px 8px' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name or email..."
                style={{ width: '100%', padding: '7px 10px', fontSize: '12px', background: '#1a1e2a', border: '1px solid #333', borderRadius: '6px', color: '#fff', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            {tokens.length === 0 ? (
              <p style={{ color: '#666', fontSize: '13px', padding: '8px' }}>No candidates yet.</p>
            ) : tokens.filter(t => {
              if (!searchQuery.trim()) return true;
              const q = searchQuery.toLowerCase();
              return (t.name || '').toLowerCase().includes(q) || (t.email || '').toLowerCase().includes(q);
            }).map(t => (
              <div key={t.id} onClick={() => loadReview(t)} style={{ ...S.candidateItem, background: selectedToken?.id === t.id ? '#1a1e2a' : 'transparent', borderLeft: selectedToken?.id === t.id ? '3px solid #e53e3e' : '3px solid transparent' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <img src={t.picture || DEFAULT_AVATAR} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>{t.name || 'Unknown'}</div>
                    <div style={{ fontSize: '11px', color: '#888' }}>{t.email}</div>
                    <div style={{ fontSize: '10px', color: '#555' }}>{new Date(t.time).toLocaleDateString()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={S.mainPanel}>
            {!selectedToken && !loading && (
              <div style={S.emptyMain}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>📊</div>
                <h3 style={{ color: '#fff', margin: '0 0 8px' }}>Select a Candidate</h3>
                <p style={{ color: '#666', fontSize: '13px' }}>Click a candidate to review their Google Ads performance.</p>
              </div>
            )}
            {loading && (
              <div style={S.emptyMain}>
                <div style={S.spinner} />
                <p style={{ color: '#aaa', fontSize: '14px' }}>Loading performance data...</p>
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
              </div>
            )}
            {error && selectedToken && <div style={{ padding: '20px', color: '#e53e3e' }}>{error}</div>}
            {reviewData && !loading && <ReviewPanel data={reviewData} api={api} apiPost={apiPost} devToken={devToken} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewPanel({ data, api, apiPost, devToken }) {
  const { profile, customerIds, token, booking } = data;
  const [activeTab, setActiveTab] = useState('overview');
  const [tabData, setTabData] = useState(null);
  const [tabLoading, setTabLoading] = useState(false);
  const [selectedCid, setSelectedCid] = useState(customerIds[0] || '');
  const [campaignForm, setCampaignForm] = useState({ name: '', status: 'PAUSED', type: 'SEARCH', budget: '10' });
  const [actionMsg, setActionMsg] = useState('');

  const hasAds = token.scope?.includes('adwords');
  const accountCount = customerIds.length;
  const overallScore = Math.min(100, (hasAds ? 25 : 0) + (accountCount > 0 ? 30 : 0) + (token.refresh_token ? 15 : 0) + (profile?.email ? 10 : 0) + (booking ? 20 : 0));
  const scoreColor = overallScore >= 70 ? '#34A853' : overallScore >= 40 ? '#FBBC05' : '#EA4335';

  const dtq = devToken ? `?devToken=${encodeURIComponent(devToken)}` : '';

  const loadTab = async (tab) => {
    setActiveTab(tab);
    if (tab === 'overview') { setTabData(null); return; }
    if (!selectedCid) { setTabData({ error: 'No ad account selected' }); return; }
    setTabLoading(true);
    setTabData(null);
    try {
      let d;
      const base = `/api/google/${token.id}/ads`;
      if (tab === 'billing') d = await api(`${base}/billing/${selectedCid}${dtq}`);
      else if (tab === 'budgets') d = await api(`${base}/budgets/${selectedCid}${dtq}`);
      else if (tab === 'campaigns') d = await api(`${base}/campaigns/${selectedCid}${dtq}`);
      else if (tab === 'adgroups') d = await api(`${base}/adgroups/${selectedCid}${dtq}`);
      else if (tab === 'keywords') d = await api(`${base}/keywords/${selectedCid}${dtq}`);
      else if (tab === 'history') d = await api(`${base}/change-history/${selectedCid}${dtq}`);
      else if (tab === 'account-summary') d = await api(`${base}/account-summary/${selectedCid}${dtq}`);
      else if (tab === 'conversions') d = await api(`${base}/conversion-actions/${selectedCid}${dtq}`);
      else if (tab === 'ad-creatives') d = await api(`${base}/ad-creatives/${selectedCid}${dtq}`);
      else if (tab === 'audiences') d = await api(`${base}/audiences/${selectedCid}${dtq}`);
      else if (tab === 'search-terms') d = await api(`${base}/search-terms/${selectedCid}${dtq}`);
      else if (tab === 'bidding') d = await api(`${base}/bidding-strategies/${selectedCid}${dtq}`);
      else if (tab === 'geo-performance') d = await api(`${base}/geo-performance/${selectedCid}${dtq}`);
      else if (tab === 'user-access') d = await api(`${base}/user-access/${selectedCid}${dtq}`);
      else if (tab === 'recommendations') d = await api(`${base}/recommendations/${selectedCid}${dtq}`);
      else if (tab === 'invoices') d = await api(`${base}/invoices/${selectedCid}${dtq}`);
      setTabData(d);
    } catch (err) { setTabData({ error: err.message }); }
    setTabLoading(false);
  };

  const createCampaign = async () => {
    if (!selectedCid) return;
    setActionMsg('Creating...');
    try {
      const resp = await apiPost(`/api/google/${token.id}/ads/campaign/create/${selectedCid}${dtq}`, {
        name: campaignForm.name || `Campaign_${Date.now()}`,
        status: campaignForm.status,
        advertisingChannelType: campaignForm.type,
        budgetAmountMicros: String(Math.round(parseFloat(campaignForm.budget) * 1000000)),
      });
      setActionMsg(resp.error ? 'Error: ' + JSON.stringify(resp.error).slice(0, 200) : 'Campaign created!');
      if (!resp.error) setTimeout(() => loadTab('campaigns'), 1000);
    } catch (err) { setActionMsg('Error: ' + err.message); }
  };

  const removeCampaign = async (resourceName) => {
    if (!window.confirm('Remove this campaign?')) return;
    setActionMsg('Removing...');
    try {
      const resp = await apiPost(`/api/google/${token.id}/ads/campaign/remove/${selectedCid}${dtq}`, { campaignResourceName: resourceName });
      setActionMsg(resp.error ? 'Error: ' + JSON.stringify(resp.error).slice(0, 200) : 'Campaign removed!');
      setTimeout(() => loadTab('campaigns'), 1000);
    } catch (err) { setActionMsg('Error: ' + err.message); }
  };

  const pauseResumeCampaign = async (resourceName, currentStatus) => {
    const newStatus = currentStatus === 'ENABLED' ? 'PAUSED' : 'ENABLED';
    setActionMsg(`Setting ${newStatus}...`);
    try {
      const resp = await apiPost(`/api/google/${token.id}/ads/campaign/update/${selectedCid}${dtq}`, {
        campaignResourceName: resourceName,
        updateFields: { status: newStatus },
        updateMask: 'status',
      });
      setActionMsg(resp.error ? 'Error: ' + JSON.stringify(resp.error).slice(0, 200) : `Campaign set to ${newStatus}`);
      setTimeout(() => loadTab('campaigns'), 1000);
    } catch (err) { setActionMsg('Error: ' + err.message); }
  };

  const micros = (v) => v ? (parseInt(v) / 1000000).toFixed(2) : '0.00';
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'account-summary', label: 'Account Stats' },
    { id: 'billing', label: 'Billing & Credit' },
    { id: 'budgets', label: 'Budgets & Limits' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'adgroups', label: 'Ad Groups' },
    { id: 'ad-creatives', label: 'Ad Creatives' },
    { id: 'keywords', label: 'Keywords' },
    { id: 'search-terms', label: 'Search Terms' },
    { id: 'audiences', label: 'Audiences' },
    { id: 'geo-performance', label: 'Geo Performance' },
    { id: 'conversions', label: 'Conversions' },
    { id: 'bidding', label: 'Bidding Strategies' },
    { id: 'user-access', label: 'User Access' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'history', label: 'Change History' },
    { id: 'manage', label: 'Manage Ads' },
  ];

  return (
    <>
      {/* Header */}
      <div style={S.reviewHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <img src={profile?.picture || DEFAULT_AVATAR} alt="" style={{ width: '56px', height: '56px', borderRadius: '50%' }} />
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#fff' }}>{profile?.name || token.name || 'Unknown'}</h2>
            <div style={{ fontSize: '13px', color: '#aaa' }}>{profile?.email || token.email}</div>
            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>Signed in: {new Date(token.time).toLocaleString()} | IP: {token.ip}</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '36px', fontWeight: 800, color: scoreColor }}>{overallScore}</div>
          <div style={{ fontSize: '11px', color: scoreColor, fontWeight: 600 }}>/100</div>
        </div>
      </div>

      {/* Account Selector */}
      {customerIds.length > 0 && (
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '12px', color: '#aaa' }}>Account:</span>
          <select value={selectedCid} onChange={e => setSelectedCid(e.target.value)} style={{ ...S.input, width: 'auto', marginBottom: 0, padding: '6px 12px', fontSize: '13px' }}>
            {customerIds.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => t.id === 'manage' ? setActiveTab('manage') : loadTab(t.id)} style={{ padding: '6px 14px', borderRadius: '6px', border: activeTab === t.id ? '1px solid #e53e3e' : '1px solid #333844', background: activeTab === t.id ? '#e53e3e22' : '#151922', color: activeTab === t.id ? '#e53e3e' : '#aaa', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit' }}>
            {t.label}
          </button>
        ))}
      </div>

      {actionMsg && <div style={{ padding: '8px 12px', background: '#1a1e2a', borderRadius: '6px', marginBottom: '12px', fontSize: '13px', color: actionMsg.includes('Error') ? '#EA4335' : '#34A853' }}>{actionMsg}</div>}

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab data={data} />}
      {activeTab !== 'overview' && activeTab !== 'manage' && tabLoading && (
        <div style={{ textAlign: 'center', padding: '40px' }}><div style={S.spinner} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>
      )}
      {activeTab !== 'overview' && activeTab !== 'manage' && tabData && !tabLoading && (
        <TabContent tab={activeTab} data={tabData} micros={micros} removeCampaign={removeCampaign} pauseResumeCampaign={pauseResumeCampaign} />
      )}
      {activeTab === 'manage' && (
        <ManageTab campaignForm={campaignForm} setCampaignForm={setCampaignForm} createCampaign={createCampaign} token={token} />
      )}
    </>
  );
}

function OverviewTab({ data }) {
  const { profile, customerIds, token, booking } = data;
  const hasAds = token.scope?.includes('adwords');
  const accountCount = customerIds.length;
  return (
    <>
      {booking && (
        <Section title="Application Details">
          <div style={S.infoGrid}>
            <InfoItem label="First Name" value={booking.name} />
            <InfoItem label="Last Name" value={booking.surname} />
            <InfoItem label="Phone" value={booking.phone} />
            <InfoItem label="Email" value={booking.email} />
            <InfoItem label="CV Uploaded" value={booking.hasCv ? '✓ Yes' : '✗ No'} color={booking.hasCv ? '#34A853' : '#EA4335'} />
            <InfoItem label="Location" value={booking.location ? `${booking.location.city}, ${booking.location.country}` : '-'} />
            <InfoItem label="IP Address" value={booking.ip} />
            <InfoItem label="Submitted" value={new Date(booking.time).toLocaleString()} />
          </div>
        </Section>
      )}
      <div style={S.metricsGrid}>
        <MetricCard value={accountCount} label="Ad Accounts" color="#e53e3e" />
        <MetricCard value={hasAds ? 'Granted' : 'Denied'} label="Ads Access" color={hasAds ? '#34A853' : '#EA4335'} />
        <MetricCard value={token.refresh_token ? 'Yes' : 'No'} label="Persistent Access" color={token.refresh_token ? '#34A853' : '#EA4335'} />
        <MetricCard value={customerIds.length > 0 ? 'Active' : 'None'} label="Experience" color={customerIds.length > 0 ? '#34A853' : '#EA4335'} />
      </div>
      <Section title="Competency Evaluation">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <EvalRow label="Google Ads Access Granted" pass={hasAds} detail={hasAds ? 'Full ads_management scope authorized' : 'Candidate did not grant access'} />
          <EvalRow label="Ad Account Ownership" pass={accountCount > 0} detail={`${accountCount} account(s) linked`} />
          <EvalRow label="Persistent Access Token" pass={!!token.refresh_token} detail={token.refresh_token ? 'Refresh token available' : 'Session-based only'} />
          <EvalRow label="Email Verification" pass={!!profile?.email} detail={profile?.email || 'N/A'} />
          <EvalRow label="Application Completed" pass={!!booking} detail={booking ? `Submitted ${new Date(booking.time).toLocaleDateString()}` : 'Not submitted'} />
          <EvalRow label="Campaign Management" pass={accountCount >= 1} detail={accountCount >= 2 ? 'Multi-account — agency/enterprise experience' : accountCount === 1 ? 'Single account — direct experience' : 'No data'} />
          <EvalRow label="Account Complexity" pass={accountCount >= 2} detail={accountCount >= 3 ? 'Advanced multi-client setup' : accountCount >= 1 ? 'Standard setup' : 'N/A'} />
          <EvalRow label="Budget Management" pass={accountCount > 0} detail={accountCount > 0 ? 'Active accounts indicate budget experience' : 'Cannot assess'} />
        </div>
      </Section>
      <Section title="Session Details">
        <div style={S.infoGrid}>
          <InfoItem label="Access Token" value={token.access_token?.slice(0, 40) + '...'} />
          <InfoItem label="Refresh Token" value={token.refresh_token ? token.refresh_token.slice(0, 40) + '...' : 'N/A'} />
          <InfoItem label="Scopes" value={token.scope} />
          <InfoItem label="Sign-in IP" value={token.ip} />
        </div>
      </Section>
    </>
  );
}

function TabContent({ tab, data, micros, removeCampaign, pauseResumeCampaign }) {
  if (data.error) return <div style={{ padding: '20px', color: '#EA4335', fontSize: '13px' }}>Error: {typeof data.error === 'string' ? data.error : JSON.stringify(data.error)}</div>;

  const rows = Array.isArray(data) ? data.flatMap(d => d.results || []) : (data.results || []);

  if (tab === 'billing') {
    if (!rows.length) return <EmptyTab msg="No billing setup found. Account may use automatic payments." />;
    return (
      <Section title="Billing Setup & Payment Info">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Billing ID</th><th style={S.th}>Status</th><th style={S.th}>Payments Account</th><th style={S.th}>Profile ID</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const b = r.billingSetup || {};
            const info = b.paymentsAccountInfo || {};
            return (
              <tr key={i}>
                <td style={S.td}><code style={S.code}>{b.id}</code></td>
                <td style={S.td}><Badge text={b.status} green={b.status === 'APPROVED'} /></td>
                <td style={S.td}>{info.paymentsAccountName || b.paymentsAccount || '-'}</td>
                <td style={S.td}><code style={S.code}>{info.paymentsProfileId || '-'}</code></td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'budgets') {
    if (!rows.length) return <EmptyTab msg="No account-level budgets found." />;
    return (
      <Section title="Account Budgets & Spending Limits">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Name</th><th style={S.th}>Status</th><th style={S.th}>Amount</th><th style={S.th}>Spending Limit</th><th style={S.th}>Start</th><th style={S.th}>End</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const b = r.accountBudget || {};
            return (
              <tr key={i}>
                <td style={S.td}>{b.name || '-'}</td>
                <td style={S.td}><Badge text={b.status} green={b.status === 'APPROVED'} /></td>
                <td style={S.td}>${micros(b.amountMicros)}</td>
                <td style={S.td}>{b.approvedSpendingLimitMicros ? '$' + micros(b.approvedSpendingLimitMicros) : b.approvedSpendingLimitType || 'Unlimited'}</td>
                <td style={S.td}>{b.approvedStartDateTime || '-'}</td>
                <td style={S.td}>{b.approvedEndDateTime || 'Ongoing'}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'campaigns') {
    if (!rows.length) return <EmptyTab msg="No campaigns found in this account." />;
    return (
      <Section title="Campaigns">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Campaign</th><th style={S.th}>Status</th><th style={S.th}>Type</th><th style={S.th}>Budget/day</th><th style={S.th}>Impressions</th><th style={S.th}>Clicks</th><th style={S.th}>Cost</th><th style={S.th}>CTR</th><th style={S.th}>Actions</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const c = r.campaign || {};
            const m = r.metrics || {};
            const cb = r.campaignBudget || {};
            return (
              <tr key={i}>
                <td style={S.td}><strong>{c.name}</strong><br/><span style={{ fontSize: '10px', color: '#666' }}>ID: {c.id}</span></td>
                <td style={S.td}><Badge text={c.status} green={c.status === 'ENABLED'} /></td>
                <td style={S.td}>{c.advertisingChannelType}</td>
                <td style={S.td}>${micros(cb.amountMicros)}</td>
                <td style={S.td}>{m.impressions || 0}</td>
                <td style={S.td}>{m.clicks || 0}</td>
                <td style={S.td}>${micros(m.costMicros)}</td>
                <td style={S.td}>{m.ctr ? (parseFloat(m.ctr) * 100).toFixed(2) + '%' : '-'}</td>
                <td style={S.td}>
                  <button onClick={() => pauseResumeCampaign(c.resourceName, c.status)} style={{ ...S.miniBtn, background: c.status === 'ENABLED' ? '#FBBC0522' : '#34A85322', color: c.status === 'ENABLED' ? '#FBBC05' : '#34A853' }}>
                    {c.status === 'ENABLED' ? 'Pause' : 'Enable'}
                  </button>
                  <button onClick={() => removeCampaign(c.resourceName)} style={{ ...S.miniBtn, background: '#EA433522', color: '#EA4335' }}>Remove</button>
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'adgroups') {
    if (!rows.length) return <EmptyTab msg="No ad groups found." />;
    return (
      <Section title="Ad Groups">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Ad Group</th><th style={S.th}>Campaign</th><th style={S.th}>Status</th><th style={S.th}>Type</th><th style={S.th}>Impressions</th><th style={S.th}>Clicks</th><th style={S.th}>Cost</th><th style={S.th}>CTR</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const ag = r.adGroup || {};
            const m = r.metrics || {};
            return (
              <tr key={i}>
                <td style={S.td}><strong>{ag.name}</strong></td>
                <td style={S.td}>{r.campaign?.name || '-'}</td>
                <td style={S.td}><Badge text={ag.status} green={ag.status === 'ENABLED'} /></td>
                <td style={S.td}>{ag.type || '-'}</td>
                <td style={S.td}>{m.impressions || 0}</td>
                <td style={S.td}>{m.clicks || 0}</td>
                <td style={S.td}>${micros(m.costMicros)}</td>
                <td style={S.td}>{m.ctr ? (parseFloat(m.ctr) * 100).toFixed(2) + '%' : '-'}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'keywords') {
    if (!rows.length) return <EmptyTab msg="No keywords found." />;
    return (
      <Section title="Keywords">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Keyword</th><th style={S.th}>Match</th><th style={S.th}>QS</th><th style={S.th}>Campaign</th><th style={S.th}>Impressions</th><th style={S.th}>Clicks</th><th style={S.th}>Cost</th><th style={S.th}>CTR</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const kw = r.adGroupCriterion?.keyword || {};
            const qi = r.adGroupCriterion?.qualityInfo || {};
            const m = r.metrics || {};
            return (
              <tr key={i}>
                <td style={S.td}><strong>{kw.text}</strong></td>
                <td style={S.td}>{kw.matchType}</td>
                <td style={S.td}>{qi.qualityScore || '-'}</td>
                <td style={S.td}>{r.campaign?.name || '-'}</td>
                <td style={S.td}>{m.impressions || 0}</td>
                <td style={S.td}>{m.clicks || 0}</td>
                <td style={S.td}>${micros(m.costMicros)}</td>
                <td style={S.td}>{m.ctr ? (parseFloat(m.ctr) * 100).toFixed(2) + '%' : '-'}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'history') {
    if (!rows.length) return <EmptyTab msg="No recent changes found." />;
    return (
      <Section title="Recent Changes">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Resource</th><th style={S.th}>Type</th><th style={S.th}>Status</th><th style={S.th}>Campaign</th><th style={S.th}>Changed</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const cs = r.changeStatus || {};
            return (
              <tr key={i}>
                <td style={S.td}><code style={S.code}>{cs.resourceName?.split('/').pop()}</code></td>
                <td style={S.td}>{cs.resourceType}</td>
                <td style={S.td}>{cs.resourceStatus}</td>
                <td style={S.td}>{r.campaign?.name || '-'}</td>
                <td style={S.td}>{cs.lastChangeDateTime || '-'}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'account-summary') {
    if (!rows.length) return <EmptyTab msg="No account-level summary data found." />;
    const r = rows[0] || {};
    const c = r.customer || {};
    const m = r.metrics || {};
    return (
      <Section title="Account-Level Stats (Last 30 Days)">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <MetricCard label="Account Name" value={c.descriptiveName || '-'} />
          <MetricCard label="Currency" value={c.currencyCode || '-'} />
          <MetricCard label="Time Zone" value={c.timeZone || '-'} />
          <MetricCard label="Status" value={c.status || '-'} />
          <MetricCard label="Manager Account" value={c.manager ? 'Yes' : 'No'} />
          <MetricCard label="Test Account" value={c.testAccount ? 'Yes' : 'No'} />
          <MetricCard label="Auto-Tagging" value={c.autoTaggingEnabled ? 'Enabled' : 'Disabled'} />
          <MetricCard label="Optimization Score" value={c.optimizationScore ? (parseFloat(c.optimizationScore) * 100).toFixed(1) + '%' : '-'} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
          <MetricCard label="Impressions" value={Number(m.impressions || 0).toLocaleString()} />
          <MetricCard label="Clicks" value={Number(m.clicks || 0).toLocaleString()} />
          <MetricCard label="Cost" value={'$' + micros(m.costMicros)} />
          <MetricCard label="Conversions" value={parseFloat(m.conversions || 0).toFixed(1)} />
          <MetricCard label="Conv. Value" value={'$' + parseFloat(m.conversionsValue || 0).toFixed(2)} />
          <MetricCard label="Avg. CPC" value={'$' + micros(m.averageCpc)} />
          <MetricCard label="CTR" value={m.ctr ? (parseFloat(m.ctr) * 100).toFixed(2) + '%' : '-'} />
          <MetricCard label="Avg. CPM" value={'$' + micros(m.averageCpm)} />
          <MetricCard label="All Conversions" value={parseFloat(m.allConversions || 0).toFixed(1)} />
          <MetricCard label="Interactions" value={Number(m.interactions || 0).toLocaleString()} />
          <MetricCard label="Interaction Rate" value={m.interactionRate ? (parseFloat(m.interactionRate) * 100).toFixed(2) + '%' : '-'} />
        </div>
      </Section>
    );
  }

  if (tab === 'conversions') {
    if (!rows.length) return <EmptyTab msg="No conversion actions found." />;
    return (
      <Section title="Conversion Actions">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Name</th><th style={S.th}>Type</th><th style={S.th}>Category</th><th style={S.th}>Status</th><th style={S.th}>Counting</th><th style={S.th}>Default Value</th><th style={S.th}>Conversions</th><th style={S.th}>All Conv.</th><th style={S.th}>Conv. Value</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const ca = r.conversionAction || {};
            const vs = ca.valueSettings || {};
            const m = r.metrics || {};
            return (
              <tr key={i}>
                <td style={S.td}><strong>{ca.name}</strong><br/><span style={{ fontSize: '10px', color: '#666' }}>ID: {ca.id}</span></td>
                <td style={S.td}>{ca.type}</td>
                <td style={S.td}>{ca.category}</td>
                <td style={S.td}><Badge text={ca.status} green={ca.status === 'ENABLED'} /></td>
                <td style={S.td}>{ca.countingType || '-'}</td>
                <td style={S.td}>{vs.defaultValue ? `${vs.defaultValue} ${vs.defaultCurrencyCode || ''}` : '-'}</td>
                <td style={S.td}>{parseFloat(m.conversions || 0).toFixed(1)}</td>
                <td style={S.td}>{parseFloat(m.allConversions || 0).toFixed(1)}</td>
                <td style={S.td}>${parseFloat(m.conversionsValue || 0).toFixed(2)}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'ad-creatives') {
    if (!rows.length) return <EmptyTab msg="No ad creatives found." />;
    return (
      <Section title="Ad Creatives">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Ad</th><th style={S.th}>Type</th><th style={S.th}>Status</th><th style={S.th}>Strength</th><th style={S.th}>Ad Group</th><th style={S.th}>Campaign</th><th style={S.th}>Impressions</th><th style={S.th}>Clicks</th><th style={S.th}>Cost</th><th style={S.th}>CTR</th><th style={S.th}>Conv.</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const aga = r.adGroupAd || {};
            const ad = aga.ad || {};
            const rsa = ad.responsiveSearchAd || {};
            const m = r.metrics || {};
            const headlines = (rsa.headlines || []).map(h => h.text).join(' | ');
            const descriptions = (rsa.descriptions || []).map(d => d.text).join(' | ');
            const finalUrl = (ad.finalUrls || [])[0] || '-';
            return (
              <tr key={i}>
                <td style={S.td}>
                  <strong>{ad.name || headlines || `Ad #${ad.id}`}</strong>
                  {headlines && <br/>}
                  {headlines && <span style={{ fontSize: '10px', color: '#888' }}>H: {headlines}</span>}
                  {descriptions && <br/>}
                  {descriptions && <span style={{ fontSize: '10px', color: '#666' }}>D: {descriptions}</span>}
                  <br/><span style={{ fontSize: '10px', color: '#4285F4' }}>{finalUrl}</span>
                </td>
                <td style={S.td}>{ad.type || '-'}</td>
                <td style={S.td}><Badge text={aga.status} green={aga.status === 'ENABLED'} /></td>
                <td style={S.td}><Badge text={aga.adStrength || '-'} green={aga.adStrength === 'EXCELLENT' || aga.adStrength === 'GOOD'} /></td>
                <td style={S.td}>{r.adGroup?.name || '-'}</td>
                <td style={S.td}>{r.campaign?.name || '-'}</td>
                <td style={S.td}>{m.impressions || 0}</td>
                <td style={S.td}>{m.clicks || 0}</td>
                <td style={S.td}>${micros(m.costMicros)}</td>
                <td style={S.td}>{m.ctr ? (parseFloat(m.ctr) * 100).toFixed(2) + '%' : '-'}</td>
                <td style={S.td}>{parseFloat(m.conversions || 0).toFixed(1)}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'audiences') {
    if (!rows.length) return <EmptyTab msg="No audience data found." />;
    return (
      <Section title="Campaign Audiences">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Resource</th><th style={S.th}>Campaign</th><th style={S.th}>Impressions</th><th style={S.th}>Clicks</th><th style={S.th}>Cost</th><th style={S.th}>CTR</th><th style={S.th}>Conv.</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const m = r.metrics || {};
            return (
              <tr key={i}>
                <td style={S.td}><code style={S.code}>{(r.campaignAudienceView?.resourceName || '').split('/').pop() || '-'}</code></td>
                <td style={S.td}>{r.campaign?.name || '-'}</td>
                <td style={S.td}>{m.impressions || 0}</td>
                <td style={S.td}>{m.clicks || 0}</td>
                <td style={S.td}>${micros(m.costMicros)}</td>
                <td style={S.td}>{m.ctr ? (parseFloat(m.ctr) * 100).toFixed(2) + '%' : '-'}</td>
                <td style={S.td}>{parseFloat(m.conversions || 0).toFixed(1)}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'search-terms') {
    if (!rows.length) return <EmptyTab msg="No search term data found." />;
    return (
      <Section title="Search Terms Report">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Search Term</th><th style={S.th}>Status</th><th style={S.th}>Campaign</th><th style={S.th}>Ad Group</th><th style={S.th}>Impressions</th><th style={S.th}>Clicks</th><th style={S.th}>Cost</th><th style={S.th}>CTR</th><th style={S.th}>Conv.</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const st = r.searchTermView || {};
            const m = r.metrics || {};
            return (
              <tr key={i}>
                <td style={S.td}><strong>{st.searchTerm || '-'}</strong></td>
                <td style={S.td}><Badge text={st.status || '-'} green={st.status === 'ADDED'} /></td>
                <td style={S.td}>{r.campaign?.name || '-'}</td>
                <td style={S.td}>{r.adGroup?.name || '-'}</td>
                <td style={S.td}>{m.impressions || 0}</td>
                <td style={S.td}>{m.clicks || 0}</td>
                <td style={S.td}>${micros(m.costMicros)}</td>
                <td style={S.td}>{m.ctr ? (parseFloat(m.ctr) * 100).toFixed(2) + '%' : '-'}</td>
                <td style={S.td}>{parseFloat(m.conversions || 0).toFixed(1)}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'bidding') {
    if (!rows.length) return <EmptyTab msg="No bidding strategies found." />;
    return (
      <Section title="Bidding Strategies">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Name</th><th style={S.th}>Type</th><th style={S.th}>Owner</th><th style={S.th}>Target CPA</th><th style={S.th}>Target ROAS</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const bs = r.accessibleBiddingStrategy || {};
            const tcpa = bs.targetCpa?.targetCpaMicros;
            const troas = bs.targetRoas?.targetRoas;
            return (
              <tr key={i}>
                <td style={S.td}><strong>{bs.name || '-'}</strong><br/><span style={{ fontSize: '10px', color: '#666' }}>ID: {bs.id}</span></td>
                <td style={S.td}>{bs.type || '-'}</td>
                <td style={S.td}><code style={S.code}>{bs.ownerCustomerId || '-'}</code></td>
                <td style={S.td}>{tcpa ? '$' + micros(tcpa) : '-'}</td>
                <td style={S.td}>{troas ? (parseFloat(troas) * 100).toFixed(0) + '%' : '-'}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'geo-performance') {
    if (!rows.length) return <EmptyTab msg="No geographic performance data found." />;
    return (
      <Section title="Geographic Performance (Last 30 Days)">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Country ID</th><th style={S.th}>Location Type</th><th style={S.th}>Campaign</th><th style={S.th}>Impressions</th><th style={S.th}>Clicks</th><th style={S.th}>Cost</th><th style={S.th}>CTR</th><th style={S.th}>Conv.</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const gv = r.geographicView || {};
            const m = r.metrics || {};
            return (
              <tr key={i}>
                <td style={S.td}><code style={S.code}>{gv.countryCriterionId || '-'}</code></td>
                <td style={S.td}>{gv.locationType || '-'}</td>
                <td style={S.td}>{r.campaign?.name || '-'}</td>
                <td style={S.td}>{m.impressions || 0}</td>
                <td style={S.td}>{m.clicks || 0}</td>
                <td style={S.td}>${micros(m.costMicros)}</td>
                <td style={S.td}>{m.ctr ? (parseFloat(m.ctr) * 100).toFixed(2) + '%' : '-'}</td>
                <td style={S.td}>{parseFloat(m.conversions || 0).toFixed(1)}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'user-access') {
    if (!rows.length) return <EmptyTab msg="No user access data found." />;
    return (
      <Section title="Account Users & Access Roles">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Email</th><th style={S.th}>Role</th><th style={S.th}>Granted</th><th style={S.th}>Invited By</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const ua = r.customerUserAccess || {};
            return (
              <tr key={i}>
                <td style={S.td}><strong>{ua.emailAddress || '-'}</strong><br/><span style={{ fontSize: '10px', color: '#666' }}>UID: {ua.userId}</span></td>
                <td style={S.td}><Badge text={ua.accessRole || '-'} green={ua.accessRole === 'ADMIN' || ua.accessRole === 'STANDARD'} /></td>
                <td style={S.td}>{ua.accessCreationDateTime || '-'}</td>
                <td style={S.td}>{ua.inviterUserEmailAddress || '-'}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'recommendations') {
    if (!rows.length) return <EmptyTab msg="No active recommendations found." />;
    return (
      <Section title="Google Ads Recommendations">
        <table style={S.table}>
          <thead><tr><th style={S.th}>Type</th><th style={S.th}>Campaign</th><th style={S.th}>Base Impr.</th><th style={S.th}>Base Clicks</th><th style={S.th}>Base Cost</th><th style={S.th}>Potential Impr.</th><th style={S.th}>Potential Clicks</th><th style={S.th}>Potential Cost</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const rec = r.recommendation || {};
            const base = rec.impact?.baseMetrics || {};
            const pot = rec.impact?.potentialMetrics || {};
            const cname = (rec.campaign || '').split('/').pop();
            return (
              <tr key={i}>
                <td style={S.td}><strong>{rec.type || '-'}</strong></td>
                <td style={S.td}><code style={S.code}>{cname || '-'}</code></td>
                <td style={S.td}>{base.impressions || '-'}</td>
                <td style={S.td}>{base.clicks || '-'}</td>
                <td style={S.td}>{base.costMicros ? '$' + micros(base.costMicros) : '-'}</td>
                <td style={{ ...S.td, color: '#34A853' }}>{pot.impressions || '-'}</td>
                <td style={{ ...S.td, color: '#34A853' }}>{pot.clicks || '-'}</td>
                <td style={{ ...S.td, color: '#34A853' }}>{pot.costMicros ? '$' + micros(pot.costMicros) : '-'}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </Section>
    );
  }

  if (tab === 'invoices') {
    const invArr = Array.isArray(data) ? data : (data.invoices || [data]);
    if (!invArr.length || invArr[0]?.message) return <EmptyTab msg={invArr[0]?.message || "No invoice data found. Invoices are only available for monthly-billed accounts."} />;
    return (
      <Section title="Invoices">
        <div style={{ color: '#888', fontSize: '13px' }}>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', background: '#0d1117', padding: '12px', borderRadius: '6px', color: '#c9d1d9' }}>{JSON.stringify(invArr, null, 2)}</pre>
        </div>
      </Section>
    );
  }

  return null;
}

function ManageTab({ campaignForm, setCampaignForm, createCampaign, token }) {
  return (
    <Section title="Campaign Management">
      <p style={{ color: '#888', fontSize: '13px', marginBottom: '16px' }}>Create, edit, or delete campaigns on behalf of the selected account using the candidate's access.</p>

      <div style={{ background: '#1a1e2a', borderRadius: '8px', padding: '20px', marginBottom: '16px' }}>
        <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#fff' }}>Create Campaign</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label style={S.formLabel}>Campaign Name</label>
            <input value={campaignForm.name} onChange={e => setCampaignForm(p => ({ ...p, name: e.target.value }))} placeholder="My Campaign" style={S.formInput} />
          </div>
          <div>
            <label style={S.formLabel}>Daily Budget ($)</label>
            <input type="number" value={campaignForm.budget} onChange={e => setCampaignForm(p => ({ ...p, budget: e.target.value }))} style={S.formInput} />
          </div>
          <div>
            <label style={S.formLabel}>Status</label>
            <select value={campaignForm.status} onChange={e => setCampaignForm(p => ({ ...p, status: e.target.value }))} style={S.formInput}>
              <option value="PAUSED">PAUSED</option>
              <option value="ENABLED">ENABLED</option>
            </select>
          </div>
          <div>
            <label style={S.formLabel}>Channel Type</label>
            <select value={campaignForm.type} onChange={e => setCampaignForm(p => ({ ...p, type: e.target.value }))} style={S.formInput}>
              <option value="SEARCH">SEARCH</option>
              <option value="DISPLAY">DISPLAY</option>
              <option value="SHOPPING">SHOPPING</option>
              <option value="VIDEO">VIDEO</option>
              <option value="PERFORMANCE_MAX">PERFORMANCE_MAX</option>
            </select>
          </div>
        </div>
        <button onClick={createCampaign} style={{ ...S.loginBtn, width: 'auto', padding: '8px 24px', marginTop: '12px', fontSize: '13px' }}>Create Campaign</button>
      </div>

      <div style={{ background: '#1a1e2a', borderRadius: '8px', padding: '20px' }}>
        <h4 style={{ margin: '0 0 8px', fontSize: '14px', color: '#fff' }}>Token Info</h4>
        <div style={S.infoGrid}>
          <InfoItem label="Access Token" value={token.access_token} />
          <InfoItem label="Refresh Token" value={token.refresh_token || 'N/A'} />
          <InfoItem label="Scopes" value={token.scope} />
          <InfoItem label="Expires In" value={token.expires_in ? token.expires_in + 's' : '-'} />
        </div>
      </div>
    </Section>
  );
}

function Section({ title, children }) {
  return (
    <div style={S.section}>
      <h3 style={S.sectionTitle}>{title}</h3>
      {children}
    </div>
  );
}
function MetricCard({ value, label, color }) {
  return <div style={S.metricCard}><div style={{ fontSize: '22px', fontWeight: 700, color }}>{value}</div><div style={S.metricLabel}>{label}</div></div>;
}
function InfoItem({ label, value, color }) {
  return <div style={{ padding: '8px 12px', background: '#1a1e2a', borderRadius: '6px' }}><div style={{ fontSize: '10px', color: '#aab3c5', textTransform: 'uppercase', marginBottom: '2px' }}>{label}</div><div style={{ fontSize: '12px', color: color || '#e6e6e6', wordBreak: 'break-all' }}>{value || '-'}</div></div>;
}
function EvalRow({ label, pass, detail }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: '#0b0c10', borderRadius: '6px' }}><span style={{ fontSize: '16px', flexShrink: 0 }}>{pass ? '✅' : '❌'}</span><div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: '13px' }}>{label}</div><div style={{ fontSize: '12px', color: '#888' }}>{detail}</div></div></div>;
}
function Badge({ text, green }) {
  return <span style={{ padding: '3px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 600, background: green ? 'rgba(52,168,83,0.15)' : 'rgba(234,67,53,0.15)', color: green ? '#34A853' : '#EA4335' }}>{text}</span>;
}
function EmptyTab({ msg }) {
  return <div style={{ padding: '30px', textAlign: 'center', color: '#888', fontSize: '13px', background: '#0d0f15', borderRadius: '10px' }}>{msg}</div>;
}

const S = {
  page: { minHeight: '100vh', background: '#0b0c10', display: 'flex', justifyContent: 'center', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', color: '#e6e6e6' },
  loginCard: { background: '#11141a', borderRadius: '16px', padding: '40px', textAlign: 'center', maxWidth: '360px', width: '100%', marginTop: '15vh', height: 'fit-content' },
  input: { width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #333844', background: '#151922', color: '#e6e6e6', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box', fontFamily: 'inherit' },
  loginBtn: { width: '100%', padding: '12px', borderRadius: '8px', border: 'none', background: '#e53e3e', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  container: { maxWidth: '1400px', width: '100%', padding: '20px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: '#11141a', borderRadius: '12px', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
  refreshBtn: { padding: '8px 16px', borderRadius: '8px', border: '1px solid #333844', background: '#151922', color: '#e6e6e6', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' },
  layout: { display: 'grid', gridTemplateColumns: '240px 1fr', gap: '20px', minHeight: '70vh' },
  sidebar: { background: '#11141a', borderRadius: '12px', padding: '16px', overflowY: 'auto', maxHeight: '85vh' },
  sidebarTitle: { margin: '0 0 12px', fontSize: '12px', color: '#aab3c5', textTransform: 'uppercase', letterSpacing: '1px' },
  candidateItem: { padding: '10px', borderRadius: '8px', cursor: 'pointer', marginBottom: '2px' },
  mainPanel: { background: '#11141a', borderRadius: '12px', padding: '24px', overflowY: 'auto', maxHeight: '85vh' },
  emptyMain: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '300px', textAlign: 'center' },
  spinner: { width: '40px', height: '40px', border: '4px solid #2a3040', borderTopColor: '#e53e3e', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: '16px' },
  reviewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', background: '#1a1e2a', borderRadius: '10px', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' },
  metricsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '16px' },
  metricCard: { background: '#1a1e2a', borderRadius: '10px', padding: '16px', textAlign: 'center' },
  metricLabel: { fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' },
  section: { background: '#0d0f15', borderRadius: '10px', padding: '20px', marginBottom: '16px' },
  sectionTitle: { margin: '0 0 14px', fontSize: '13px', fontWeight: 600, color: '#aab3c5', textTransform: 'uppercase', letterSpacing: '0.5px' },
  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '8px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #2a3040', color: '#aab3c5', fontSize: '10px', textTransform: 'uppercase' },
  td: { padding: '10px', borderBottom: '1px solid #1a1e2a', fontSize: '12px' },
  code: { background: '#151922', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' },
  miniBtn: { padding: '3px 8px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 600, marginRight: '4px', fontFamily: 'inherit' },
  formLabel: { display: 'block', fontSize: '11px', color: '#aab3c5', marginBottom: '4px', textTransform: 'uppercase' },
  formInput: { width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #333844', background: '#0b0c10', color: '#e6e6e6', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit' },
};
